import { useContext } from 'react';
import { Card, CardContent, Typography, Box, Chip, IconButton } from '@mui/material';
import { Public, Lock, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CollectionWithRole } from '../types/collection';
import { getCatalogDisplayName } from '../utils/stringHelpers';
import { PermissionsContext } from '../contexts/complexContexts';
import { ActiveCatalogContext } from '../contexts/ActiveCatalogContext';

interface CollectionCardProps {
  collection: CollectionWithRole;
  onSelect: (collectionId: string) => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({ collection, onSelect }) => {
  const navigate = useNavigate();
  // Read contexts directly (not the throwing hooks) so the card renders in
  // isolated tests / non-provider contexts; the real app always provides them.
  const permissions = useContext(PermissionsContext)?.permissions ?? null;
  const activeCatalogCtx = useContext(ActiveCatalogContext);
  const availableCatalogs = activeCatalogCtx?.availableCatalogs ?? [];
  const manageableCatalogs = activeCatalogCtx?.manageableCatalogs ?? [];
  const visibilityIcon = {
    public: <Public fontSize="small" />,
    private: <Lock fontSize="small" />
  };

  // Which catalog this album belongs to, for a badge on the cross-catalog
  // Albums page. Albums are owner-keyed (no per-catalog id), so this resolves to
  // the owner's ALI: own albums -> the caller's own ALI (assignedId); delegated
  // albums -> the delegator's catalog ALI via the availableCatalogs owner map.
  const catalogAliBadge =
    permissions?.userId && collection.ownerId === permissions.userId
      ? permissions.assignedId ?? null
      : availableCatalogs.find(c => c.ownerUserId === collection.ownerId)?.catalogAliId ?? null;

  // GH#1234: the cross-catalog union clears `role` on delegated albums, so
  // `role === 'owner'` alone hid the settings gear from FULL delegates who the
  // backend WOULD authorize (delegation-aware require_owner). Also show the
  // gear when the album's owner is one of the caller's manageable catalogs —
  // exactly the active full delegations (view-only/expired never appear there).
  const canManageAlbum =
    collection.role === 'owner' ||
    manageableCatalogs.some(
      m => m.ownerUserId !== null && m.ownerUserId === collection.ownerId,
    );

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/albums/${collection.collectionId}/settings`);
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
      onClick={() => onSelect(collection.collectionId)}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
          <Typography variant="h6" component="div">
            {getCatalogDisplayName(collection.name)}
          </Typography>
          {canManageAlbum && (
            <IconButton
              size="small"
              onClick={handleSettingsClick}
              sx={{ ml: 0.5 }}
              aria-label="album settings"
            >
              <Settings fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
          {collection.isDefault && (
            <Chip label="Default" size="small" />
          )}
          {catalogAliBadge && (
            <Chip
              label={catalogAliBadge}
              size="small"
              variant="outlined"
              aria-label={`catalog ${catalogAliBadge}`}
            />
          )}
          <Chip
            icon={visibilityIcon[collection.visibility as 'public' | 'private']}
            label={collection.visibility}
            size="small"
            variant="outlined"
          />
        </Box>

        {collection.description && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {collection.description}
          </Typography>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {collection.itemCount} {collection.itemCount === 1 ? 'item' : 'items'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Created {new Date(collection.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
